<template>
  <div class="governance-view">
    <div class="governance-header">
      <h1>Governance</h1>
      <button v-if="canCreateProposal" class="btn-create" @click="openCreateModal">
        + Create Proposal
      </button>
    </div>

    <!-- Filters -->
    <div class="filters">
      <div class="filter-group">
        <label>Type</label>
        <select v-model="filters.type" @change="applyFilters">
          <option value="">All Types</option>
          <option value="poll">Poll</option>
          <option value="budget">Budget</option>
          <option value="decision">Decision</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Status</label>
        <select v-model="activeTab" @change="applyFilters">
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
      </div>

     

      <button v-if="hasActiveFilters" class="btn-clear" @click="clearFilters">
        Clear Filters
      </button>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button 
        class="tab" 
        :class="{ active: activeTab === 'active' }"
        @click="activeTab = 'active'"
      >
        Active ({{ activeProposalsCount }})
      </button>
      <button 
        class="tab" 
        :class="{ active: activeTab === 'completed' }"
        @click="activeTab = 'completed'"
      >
        Completed ({{ completedProposalsCount }})
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading">
      <p>Loading proposals...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="loadProposals">Retry</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="displayedProposals.length === 0" class="empty-state">
      <p>No proposals found</p>
      <button class="btn-secondary" @click="openCreateModal">Create One</button>
    </div>

    <!-- Proposals List -->
    <div v-else class="proposals-list">
      <div 
        v-for="proposal in displayedProposals" 
        :key="proposal.id"
        class="proposal-item"
        @click="viewProposal(proposal)"
      >
        <div class="proposal-type-badge" :class="getTypeId(proposal)">
          {{ getTypeLabel(proposal) }}
        </div>
        <div class="proposal-content">
          <h3>{{ proposal.title }}</h3>
          <p class="proposal-description">{{ proposal.description }}</p>
          <div class="proposal-meta">
            <span class="meta-item">
              Created by: {{ getCreatorName(proposal) }}
            </span>
            <span class="meta-item">
              Votes: {{ getTotalVotes(proposal) }}
            </span>
            <span class="meta-item status" :class="proposal.status">
              {{ proposal.status }}
            </span>
          </div>
        </div>
        <div v-if="canDeleteProposal" class="proposal-actions">
          <button class="btn-delete" @click.stop="confirmDelete(proposal)">
            Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Create Proposal Modal -->
    <CreateProposal 
      v-if="showCreateModal"
      @close="closeCreateModal"
      @created="onProposalCreated"
    />

    <!-- Proposal Detail Modal -->
    <ProposalDetail 
      v-if="selectedProposal && showDetailModal"
      :proposal="selectedProposal"
      @close="closeDetailModal"
      @vote="onVote"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import CreateProposal from './CreateProposal.vue'
import ProposalDetail from './ProposalDetail.vue'

export default {
  name: 'GovernanceView',
  components: {
    CreateProposal,
    ProposalDetail
  },
  setup() {
    const store = useStore()
    const showCreateModal = ref(false)
    const canCreateProposal = computed(() => !store.getters['auth/isUser'])
    const canDeleteProposal = computed(() => !store.getters['auth/isUser'])
    const showDetailModal = ref(false)
    const selectedProposal = ref(null)
    const activeTab = ref('active')
    const filters = ref({
      type: '',
      mine: false
    })

    const isLoading = computed(() => store.state.governance.loading)
    const error = computed(() => store.state.governance.error)
    const allProposals = computed(() => store.state.governance.items)

    const activeProposals = computed(() => 
      allProposals.value.filter(p => p.status === 'active')
    )

    const completedProposals = computed(() => 
      allProposals.value.filter(p => p.status === 'completed')
    )

    const activeProposalsCount = computed(() => activeProposals.value.length)
    const completedProposalsCount = computed(() => completedProposals.value.length)

    const hasActiveFilters = computed(() => 
      filters.value.type !== '' || filters.value.mine !== false
    )

    const displayedProposals = computed(() => {
      const tab = activeTab.value === 'active' ? activeProposals.value : completedProposals.value
      
      return tab.filter(p => {
        if (filters.value.type && getTypeId(p) !== filters.value.type) return false
        if (filters.value.mine && p.created_by !== store.state.auth.user?.id) return false
        return true
      })
    })

    const getTypeId = (p) => (p?.proposal_type_id || p?.proposal_type || '').toString().toLowerCase()

    const getTypeLabel = (p) => {
      const id = getTypeId(p)
      const map = { poll: 'Poll', budget: 'Budget', decision: 'Decision' }
      return map[id] || (p?.proposal_type || id)
    }

    const getCreatorName = (p) => p?.metadata?.creator_name || p?.created_by_name || p?.created_by

    const getTotalVotes = (p) => {
      if (p?.metadata && typeof p.metadata.total_votes === 'number') return p.metadata.total_votes
      const tally = p?.vote_tally || { for: 0, against: 0, abstain: 0 }
      return (tally.for || 0) + (tally.against || 0) + (tally.abstain || 0)
    }

    const loadProposals = async () => {
      try {
        await store.dispatch('governance/fetchProposals')
      } catch (e) {
        console.error('Failed to load proposals:', e)
      }
    }

    const openCreateModal = () => {
      showCreateModal.value = true
    }

    const closeCreateModal = () => {
      showCreateModal.value = false
    }

    const onProposalCreated = () => {
      closeCreateModal()
      loadProposals()
    }

    const selectProposal = (proposal) => {
      selectedProposal.value = proposal
    }

    const viewProposal = async (proposal) => {
      const typeId = getTypeId(proposal)
      // For budget proposals, use data from the list response (includes proposal_data)
      if (typeId === 'budget') {
        selectedProposal.value = proposal
        showDetailModal.value = true
        return
      }
      try {
        const full = await store.dispatch('governance/fetchProposal', proposal.id)
        // Deep-merge key sub-objects to avoid losing metadata.has_voted and proposal_data
        selectedProposal.value = {
          ...(proposal || {}),
          ...(full || {}),
          metadata: { ...(proposal?.metadata || {}), ...(full?.metadata || {}) },
          proposal_data: { ...(proposal?.proposal_data || {}), ...(full?.proposal_data || {}) }
        }
      } catch (_) {
        selectedProposal.value = proposal
      } finally {
        showDetailModal.value = true
      }
    }

    const confirmDelete = async (proposal) => {
      const ok = window.confirm('Are you sure to delete it?')
      if (!ok) return
      try {
        await store.dispatch('governance/deleteProposal', proposal.id)
        // If the deleted item was open in details, close it
        if (selectedProposal.value?.id === proposal.id) {
          showDetailModal.value = false
          selectedProposal.value = null
        }
      } catch (e) {
        console.error('Failed to delete proposal:', e)
      }
    }

    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedProposal.value = null
    }

    const onVote = async () => {
      // Keep the details view open and refresh this proposal only
      try {
        if (selectedProposal.value?.id) {
          const updated = await store.dispatch('governance/fetchProposal', selectedProposal.value.id)
          selectedProposal.value = updated
        } else {
          await loadProposals()
        }
      } catch (e) {
        console.error('Failed to refresh proposal after vote:', e)
      }
    }

    const applyFilters = () => {
      // Filters are computed, no action needed
    }

    const clearFilters = () => {
      filters.value = {
        type: '',
        mine: false
      }
    }

    onMounted(() => {
      loadProposals()
    })

    return {
      showCreateModal,
      canCreateProposal,
      canDeleteProposal,
      showDetailModal,
      selectedProposal,
      activeTab,
      filters,
      isLoading,
      error,
      allProposals,
      activeProposalsCount,
      completedProposalsCount,
      hasActiveFilters,
      displayedProposals,
      getTypeId,
      getTypeLabel,
      getCreatorName,
      getTotalVotes,
      loadProposals,
      openCreateModal,
      closeCreateModal,
      onProposalCreated,
      selectProposal,
      viewProposal,
      confirmDelete,
      closeDetailModal,
      onVote,
      applyFilters,
      clearFilters
    }
  }
}
</script>

<style scoped>
.governance-view {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.governance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.governance-header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.btn-create {
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-create:hover {
  background: #5568d3;
}

.filters {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-group label {
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  text-transform: uppercase;
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 14px;
  color: #37352f;
  background: white;
  cursor: pointer;
}

.filter-group.checkbox {
  flex-direction: row;
  align-items: center;
}

.filter-group.checkbox label {
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: none;
  margin: 0;
}

.filter-group.checkbox input {
  cursor: pointer;
}

.btn-clear {
  padding: 8px 12px;
  background: #f3f4f6;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 13px;
  color: #6c757d;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-clear:hover {
  background: #e9ecef;
}

.tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 20px;
}

.tab {
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
}

.tab:hover {
  color: #37352f;
}

.tab.active {
  color: #667eea;
  border-bottom-color: #667eea;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.error-message {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  padding: 16px;
  color: #c00;
  margin-bottom: 16px;
}

.error-message button {
  margin-top: 8px;
  padding: 6px 12px;
  background: #c00;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.empty-state p {
  font-size: 16px;
  margin-bottom: 16px;
}

.btn-secondary {
  padding: 8px 16px;
  background: #f3f4f6;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 14px;
  color: #37352f;
  cursor: pointer;
}

.btn-secondary:hover {
  background: #e9ecef;
}

.proposals-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.proposal-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.proposal-item:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.proposal-type-badge {
  flex-shrink: 0;
  width: 80px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  color: white;
}

.proposal-type-badge.poll {
  background: #3b82f6;
}

.proposal-type-badge.budget {
  background: #f59e0b;
}

.proposal-type-badge.decision {
  background: #8b5cf6;
}

.proposal-content {
  flex: 1;
  min-width: 0;
}

.proposal-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.proposal-content h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.proposal-description {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #6c757d;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.proposal-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #6c757d;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
}

.meta-item.status {
  padding: 2px 8px;
  border-radius: 3px;
  font-weight: 500;
}

.meta-item.status.active {
  background: #dcfce7;
  color: #166534;
}

.meta-item.status.completed {
  background: #e5e7eb;
  color: #6b7280;
}


.btn-delete {
  flex-shrink: 0;
  padding: 8px 12px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  font-size: 13px;
  color: #b91c1c;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-delete:hover {
  background: #fecaca;
}
</style>
